import React, {useContext, useMemo, useRef, useState} from 'react';
import {IndividualContext} from "../../individual-context.jsx";
import {Card} from "../../../../../../components/card/card.jsx";
import {getAssetPath} from "../../../../../../utils/get-asset-path.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import optionsUtils from "../../../../../../utils/options-utils.js";
import useFetchData from "../../hooks/use-fetch-data.js";
import ModalAttaches from "../../../../../../components/modal/modal-attaches.jsx";
import PrintTemplate from "../../../../../../components/reports/print-reports/print-template.jsx";
import {imgExtensions} from "../../../../../../components/dropzone/dropzone.jsx";
import ResumeTable from "./resume-table.jsx";
import {isImageAttach} from "../../resources/attach-utils.js";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;
const API_URL = import.meta.env.VITE_API_URL;

const IndividualResume = ({printRef, headerSelected, attachesDescription = []}) => {
    const {individual, individualInfo, boName, permissions, attachList} = useContext(IndividualContext);
    const {bodyShape, colorEyes, hairType, hairColor, hairSize, situations, individualColors, instructions} = useFetchData();
    const [modal, setModal] = useState({show: false, file: null});
    const gridRef = useRef(undefined);

    const findValue = (value, data) => {
        if (!value || !Array.isArray(data)) return "";
        return data.find(item => item?.value?.toString() === value?.toString())?.label ?? "";
    }

    const openModal = (attachId) => {
        if (!attachId) return;
        const file = individual.attachs?.find(item => item.attachId === attachId) ?? {attachId, extension: "jpeg"};
        setModal({show: true, file});
    }

    const closeModal = () => {
        setModal({show: false, file: undefined});
    }

    const occurrences = individualInfo.occurrences ?? [];
    const bos = individualInfo.bos ?? [];
    const encounters = individualInfo.encounters ?? [];
    const sos = individualInfo.sos ?? [];
    const addressList = individual.addresses ?? [];
    const phoneList = individual.phones ?? [];
    const articleList = individual.articles ?? [];
    const characteristicsList = individual.individualRegionWrapper ?? [];

    const attaches = attachList.filter(item => item.attachId !== individual.relatedPhotoFaceId);
    const photoList = attaches.filter(item => isImageAttach(item));
    const fileList = attaches.filter(item => !isImageAttach(item));

    const encounterColumns = [
        {headerName: "Abordado por", field: "userNameWar"},
        {headerName: "D/H inicial", field: "dtStart", render: row => projectUtils.formatDate(row.dtStart)},
        {headerName: "Observação", field: "observation"},
    ];
    const occurrenceColumns = [
        {headerName: "Classificação", field: "classification"},
        {headerName: "Naturezas", field: "reason"},
        {headerName: "D/H Inicial do fato", field: "dtIni", render: row => projectUtils.formatDate(row.dtIni)},
        {headerName: "D/H Final do fato", field: "dtEnd", render: row => projectUtils.formatDate(row.dtEnd)},
    ];
    const boColumns = [
        {headerName: "Número", field: "id"},
        {headerName: "Status", field: "status"},
        {headerName: "Natureza(s)", field: "reason"},
        {headerName: "D/H do fato", field: "dtfact"},
    ];
    const sosColumns = [
        {headerName: "Nome", field: "name"},
        {headerName: "CPF/CNPJ", field: "cpf", render: row => row.cpf ? projectUtils.formatCpfToView(row.cpf) : projectUtils.formatCnpjToView(row.cnpj)},
        {headerName: "Programa de proteção", field: "protectionProgram", render: row => row.protectionProgram?.map(item => item.name).join(", ") ?? ""},
    ];

    const imageSrc = useMemo(() => {
        if (individual.relatedPhotoFaceId) return `${API_KEEPER}/ged/attach/${individual.relatedPhotoFaceId}`;
        return getAssetPath("img/user.png");
    }, [individual.relatedPhotoFaceId]);

    const attachSection = (title, list) => {
        if (!list.length) return null;

        return (
            <article className={"space-y-3"}>
                <h2 className={"resume-title"}>{title}</h2>

                <div className={"grid grid-cols-12 gap-3 items-center"}>
                    {
                        list.map((item, index) => {
                            const ext = item.type ?? item.extension
                            let url = !item.new ? API_KEEPER + "/ged/attach/" + item.attachId : item.url;
                            if (!imgExtensions.includes(ext)) {
                                url = API_URL + "/public/assets/img/file-types/" + item.name?.split('.').pop() + '.png';
                            }
                            return (
                                <div className={"col-span-2"} key={item.attachId ?? index}>
                                    <div className={"image"}>
                                        <img
                                            onClick={() => openModal(item.attachId)}
                                            src={url}
                                            alt=""
                                        />
                                    </div>
                                    <p className={"col-span-2"}>
                                        Descrição: <span className={"info"}>{attachesDescription[item.attachId] ?? item.description ?? ""}</span>
                                    </p>
                                </div>
                            )
                        })
                    }
                </div>
            </article>
        )
    }

    return (
        <>
            <Card className={"w-5xl justify-self-center"}>
                <Card.Body className={"!p-6"}>
                    <PrintTemplate title={individual.name || "Sem nome"} ref={printRef} entity={headerSelected} printClassName={"resume-content"}>
                        <div className={"space-y-4"}>
                            <article className={"space-y-4"}>
                                <h2 className={"resume-title"}>
                                    Identificação do indivíduo
                                </h2>

                                <section className={"grid grid-cols-12 gap-3"}>
                                    <div className={"col-span-4"}>
                                        <div className={"image"}>
                                            <img
                                                onClick={() => openModal(individual.relatedPhotoFaceId)}
                                                src={imageSrc}
                                                alt=""
                                            />
                                        </div>
                                    </div>

                                    <div className={"col-span-8 grid grid-cols-2 gap-3 self-start"}>
                                        <p>Nome: <span className={"info"}>{individual.name}</span></p>
                                        <p>Nome social: <span className={"info"}>{individual.socialName}</span></p>
                                        <p>Apelido: <span className={"info"}>{individual.nickname}</span></p>
                                        <p>CPF: <span className={"info"}>{projectUtils.formatCpfToView(individual.cpf)}</span></p>
                                        <p>RG: <span className={"info"}>{individual.rg}</span></p>
                                        <p>Emissor do RG: <span className={"info"}>{individual.emitterRg}</span></p>
                                        <p>RG criminal: <span className={"info"}>{individual.criminalRg}</span></p>
                                        <p>CNH: <span className={"info"}>{individual.cnh}</span></p>
                                        <p>Nascimento: <span className={"info"}>{projectUtils.formatDate(individual.dtBirth, "DD/MM/YYYY")}</span></p>
                                        <p>Sexo: <span className={"info"}>{individual.sex}</span></p>
                                        <p>Estado civil: <span className={"info"}>{findValue(individual.maritalStatus, optionsUtils.maritalStatus)}</span></p>
                                        <p>Nacionalidade: <span className={"info"}>{individual.nationality}</span></p>
                                        <p>Naturalidade: <span className={"info"}>{individual.cityOfBirth}</span></p>
                                        <p>Nome da mãe: <span className={"info"}>{individual.mother}</span></p>
                                        <p>Nome do pai: <span className={"info"}>{individual.father}</span></p>
                                        <div className={"col-span-2 grid grid-cols-2 gap-3 border-t border-gray-200 pt-2"}>
                                            <p>Possui transtorno mental: <span className={"info"}>{individual.mentalDisorder ? "Sim" : "Não"}</span></p>
                                            <p>Dependência álcool/drogas: <span className={"info"}>{individual.alcoholDrugDependency ? "Sim" : "Não"}</span></p>
                                        </div>
                                    </div>
                                </section>
                            </article>

                            <article className={"space-y-3"}>
                                <h2 className={"resume-title"}>Características</h2>

                                <section className={"grid grid-cols-3 gap-3"}>
                                    <p>Altura: <span className={"info"}>{individual.height ? `${individual.height} cm` : ""}</span></p>
                                    <p>Cútis: <span className={"info"}>{findValue(individual.color, individualColors)}</span></p>
                                    <p>Compleição: <span className={"info"}>{findValue(individual.bodyShape, bodyShape)}</span></p>
                                    <p>Cor dos olhos: <span className={"info"}>{findValue(individual.eyes, colorEyes)}</span></p>
                                    <p>Tipo dos cabelos: <span className={"info"}>{findValue(individual.hairType, hairType)}</span></p>
                                    <p>Cor dos cabelos: <span className={"info"}>{findValue(individual.hairColor, hairColor)}</span></p>
                                    <p>Comprimento dos cabelos: <span className={"info"}>{findValue(individual.hairSize, hairSize)}</span></p>
                                </section>
                            </article>

                            {
                                !!characteristicsList.length &&
                                <article className={"space-y-3"}>
                                    <h2 className={"resume-title"}>Outras características</h2>

                                    {
                                        characteristicsList.map(item => (
                                            <div className={"grid grid-cols-3 gap-3"} key={item.itemId}>
                                                <p>Local: <span className={"info"}>{item.local}</span></p>
                                                <p>Tipo: <span className={"info"}>{item.type}</span></p>
                                                <p className={"col-span-3"}>Descrição: <span className={"info"}>{item.description}</span></p>
                                            </div>
                                        ))
                                    }
                                </article>
                            }

                            <article className={"space-y-3"}>
                                <h2 className={"resume-title"}>Outras informações</h2>

                                <section className={"grid grid-cols-2 gap-3"}>
                                    <p>Função no crime: <span className={"info"}>{individual.roleCrime}</span></p>
                                    <p>Situação: <span className={"info"}>{findValue(individual.situation, situations)}</span></p>
                                    <p>Rede social: <span className={"info"}>{individual.socialNetwork}</span></p>
                                    <p>Grau de instrução: <span className={"info"}>{findValue(individual.educationDegree, instructions)}</span></p>
                                    <p>Profissão: <span className={"info"}>{individual.occupation}</span></p>
                                    <p className={"col-span-2"}>Informações: <span className={"info"}>{individual.information}</span></p>
                                </section>
                            </article>

                            <article className={"space-y-3"}>
                                <h2 className={"resume-title"}>Endereços</h2>
                                <section className={"grid grid-cols-2 gap-3"}>
                                    <p>Situação de rua: <span className={"info"}>{individual.homeless ? "Sim" : "Não"}</span></p>
                                </section>
                                {
                                    addressList.map(item => (
                                        <div className={"grid grid-cols-3 gap-3"} key={item.itemId}>
                                            <p>Tipo: <span className={"info"}>{item.type}</span></p>
                                            <p>Logradouro: <span className={"info"}>{item.address}</span></p>
                                            <p>Número: <span className={"info"}>{item.number}</span></p>
                                            <p>Bairro: <span className={"info"}>{item.neighborhood}</span></p>
                                            <p>Município: <span className={"info"}>{item.city}</span></p>
                                            <p>CEP: <span className={"info"}>{item.zipCode}</span></p>
                                            <p>Complemento: <span className={"info"}>{item.complement}</span></p>
                                            <p>Data: <span className={"info"}>{projectUtils.formatDate(item.date, "DD/MM/YYYY")}</span></p>
                                        </div>
                                    ))
                                }
                            </article>

                            {
                                !!phoneList.length &&
                                <article className={"space-y-3"}>
                                    <h2 className={"resume-title"}>Telefones</h2>

                                    {
                                        phoneList.map(item => (
                                            <div className={"grid grid-cols-2 gap-3"} key={item.itemId}>
                                                <p>Tipo: <span className={"info"}>{item.type}</span></p>
                                                <p>Número: <span className={"info"}>{projectUtils.formatPhone(item.data)}</span></p>
                                            </div>
                                        ))
                                    }
                                </article>
                            }

                            {
                                !!articleList.length &&
                                <article className={"space-y-3"}>
                                    <h2 className={"resume-title"}>Artigos indiciados</h2>

                                    {
                                        articleList.map(item => (
                                            <div className={"grid grid-cols-3 gap-3"} key={item.itemId}>
                                                <p>Data: <span className={"info"}>{projectUtils.formatDate(item.date, "DD/MM/YYYY")}</span></p>
                                                <p>Boletim: <span className={"info"}>{item.bo}</span></p>
                                                <p>Natureza: <span className={"info"}>{item.article}</span></p>
                                            </div>
                                        ))
                                    }
                                </article>
                            }

                            {attachSection("Anexos", fileList)}

                            {attachSection("Fotos", photoList)}

                            <article className={"space-y-3"}>
                                <h2 className={"resume-title"}>Associações</h2>
                                {
                                    (!encounters.length && !occurrences.length && !bos.length && !sos.length) &&
                                    <div className={"grid grid-cols-12 gap-3 items-center"}>
                                        <div className={"col-span-12"}>
                                            <span className={"info"}></span>
                                        </div>
                                    </div>
                                }

                                {permissions.encounterPermission && <ResumeTable title={"Abordagens"} columns={encounterColumns} rows={encounters}/>}
                                {permissions.occPermission && <ResumeTable title={"Ocorrências"} columns={occurrenceColumns} rows={occurrences}/>}
                                {permissions.boPermission && <ResumeTable title={boName} columns={boColumns} rows={bos}/>}
                                {permissions.sosPermission && <ResumeTable title={"SOS"} columns={sosColumns} rows={sos}/>}
                            </article>
                        </div>
                    </PrintTemplate>
                </Card.Body>
            </Card>

            {
                modal.show &&
                <ModalAttaches
                    open={modal.show}
                    setOpen={closeModal}
                    file={modal.file}
                    src={modal?.file?.attachId ? `${API_URL}/ged/attach/${modal.file.attachId}` : undefined}
                    extension={modal?.file?.extension ?? "jpeg"}
                    gridRef={gridRef}
                />
            }
        </>
    );
};

export default IndividualResume;
